import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PlanType, subscriptionPlans } from '../config/stripe';
import './StripePaymentForm.css';
import { apiUrl } from '../config/api';

interface StripePaymentFormProps {
  selectedPlan: PlanType;
  onSuccess: (subscriptionId: string) => void;
  onCancel: () => void;
  email: string;
}

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  selectedPlan,
  onSuccess,
  onCancel,
  email
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promotionCode, setPromotionCode] = useState('');

  // Plan details are handled in the parent step; hidden in this form

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found');
      setIsProcessing(false);
      return;
    }

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: { email }
      });

      if (pmError || !paymentMethod) {
        setError(pmError?.message || 'Failed to create payment method');
        return;
      }

      const plan = subscriptionPlans[selectedPlan];

      const response = await fetch(apiUrl('/api/create-subscription'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          planId: plan.id,
          planType: selectedPlan,
          email,
          userId: null,
          promotionCode: promotionCode.trim() || undefined
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to create subscription');
      }

      const data = await response.json();
      const subscriptionId = data.subscriptionId as string;
      if (!subscriptionId) {
        throw new Error('Subscription ID missing from server response');
      }

      onSuccess(subscriptionId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="stripe-payment-form">

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Promo code (optional)</label>
          <input
            type="text"
            placeholder="Enter promo code"
            value={promotionCode}
            onChange={(e) => setPromotionCode(e.target.value)}
            className="input"
            disabled={isProcessing}
          />
        </div>
        <div className="form-group">
          <label>Credit or debit card</label>
          <div className="card-element-container">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
            />
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="button-group">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary plausible-event-name=onboarding-step-3-click-back-to-step-2"
            disabled={isProcessing}
          >
            Back
          </button>
          <button
            type="submit"
            className="btn btn-primary plausible-event-name=onboarding-step-3-click-subscribe"
            disabled={!stripe || isProcessing}
          >
            {isProcessing ? 'Processing...' : `Subscribe`}
          </button>
        </div>
      </form>

      <div className="legal-text">
        <p>Your subscription will automatically renew. You can cancel anytime in your account settings.</p>
        <p>By subscribing, you agree to our Terms of Service and Privacy Policy.</p>
      </div>
    </div>
  );
};

export default StripePaymentForm; 